import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TeamingUserConnectionFailure } from 'features/rns/interfaces/';
import { isHandoffStartedSelector } from 'features/teaming/teaming-base/store';
import { isRegularConferenceTypeSelector } from 'features/call/call-conference/store';
import { isPrimaryHearingConnectedSelector } from 'features/call/call-hearing/store';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { RnsEventBus } from 'features/call/call-events';
import { showNotification } from 'features/notification/store';
import { useWhileComponentMounted } from 'features/utils/hook';

const USER_CONNECTION_SYNC_TIMEOUT = 15000;

export const useTeamingUserConnectionStatusHandlers = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'teamNotification',
  });
  const dispatch = useAppDispatch();
  const whileComponentMounted = useWhileComponentMounted();
  const [isUserStatusReceived, setIsUserStatusReceived] = useState(false);

  const isHearingConnected = useAppSelector(isPrimaryHearingConnectedSelector);
  const isRegularConference = useAppSelector(isRegularConferenceTypeSelector);
  const isHandoffStarted = useAppSelector(isHandoffStartedSelector);

  const handleUserConnectionSuccess = () => {
    setIsUserStatusReceived(true);
  };

  const handleUserConnectionFailed = () => {
    setIsUserStatusReceived(true);
    dispatch(
      showNotification({
        severity: 'warning',
        title: t('connectionIssue'),
      })
    );
  };

  const handleTeamingUserCouldNotConnectToTeaming = (
    data: TeamingUserConnectionFailure
  ) => {
    const { wasAcceptingAgent } = data;
    setIsUserStatusReceived(true);
    dispatch(
      showNotification({
        severity: 'error',
        title: wasAcceptingAgent
          ? t('requestedInterpreterCouldNotConnect')
          : t('originalInterpreterCouldNotConnect'),
      })
    );
  };

  useEffect(() => {
    if ((isHandoffStarted || isRegularConference) && !isHearingConnected) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsUserStatusReceived(true);
    }
  }, [isHearingConnected, isRegularConference, isHandoffStarted]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setIsUserStatusReceived(true),
      USER_CONNECTION_SYNC_TIMEOUT
    );

    return () => {
      clearTimeout(timeout);
    };
  }, [setIsUserStatusReceived]);

  useEffect(() => {
    const teamingUserConnectionSuccessSubscription =
      RnsEventBus.$teamingUserConnectionSuccess
        .pipe(whileComponentMounted())
        .subscribe(handleUserConnectionSuccess);

    const teamingUserConnectionFailedSubscription =
      RnsEventBus.$teamingUserConnectionFailed
        .pipe(whileComponentMounted())
        .subscribe(handleUserConnectionFailed);

    const teamingUserCouldNotConnectToTeamingSubscription =
      RnsEventBus.$teamingUserCouldNotConnectToTeaming
        .pipe(whileComponentMounted())
        .subscribe(handleTeamingUserCouldNotConnectToTeaming);
    // Subscribe to the TeamingUserCouldNotConnectToTeaming event

    return () => {
      teamingUserConnectionSuccessSubscription.unsubscribe();
      teamingUserConnectionFailedSubscription.unsubscribe();
      teamingUserCouldNotConnectToTeamingSubscription.unsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isUserStatusReceived };
};
